import { useCallback, useEffect, useRef, useState } from 'react'
import { FanslyError, type FanslyApi } from './api'
import { useFanslyApi } from './deps'

export type QueryStatus = 'loading' | 'ready' | 'error'

export interface FanslyQuery<T> {
  status: QueryStatus
  data: T | undefined
  error: FanslyError | null
  /** Ask again; the current data stays on screen until the answer replaces it. */
  reload: () => void
  /** Patch the data in place (an optimistic edit). */
  setData: (update: (prev: T | undefined) => T | undefined) => void
  /** A reload is out while data is shown. */
  refreshing: boolean
}

export function toFanslyError(e: unknown): FanslyError {
  if (e instanceof FanslyError) return e
  return new FanslyError(e instanceof Error && e.message ? e.message : 'Something went wrong.', 0)
}

/**
 * One Fansly read for a page: loads when `key` is set (and again when it
 * changes), drops answers that arrive after a newer request or unmount.
 * `key` null = do not load yet.
 */
export function useFanslyQuery<T>(key: string | null, load: (api: FanslyApi, signal: AbortSignal) => Promise<T>): FanslyQuery<T> {
  const api = useFanslyApi()
  const [state, setState] = useState<{ status: QueryStatus; data: T | undefined; error: FanslyError | null; refreshing: boolean }>({
    status: 'loading',
    data: undefined,
    error: null,
    refreshing: false,
  })
  const loadRef = useRef(load)
  loadRef.current = load
  const seq = useRef(0)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    if (key === null) return
    const mine = ++seq.current
    const ctl = new AbortController()
    setState((s) => (s.data === undefined ? { status: 'loading', data: undefined, error: null, refreshing: false } : { ...s, refreshing: true }))
    loadRef
      .current(api, ctl.signal)
      .then((data) => {
        if (seq.current === mine) setState({ status: 'ready', data, error: null, refreshing: false })
      })
      .catch((e: unknown) => {
        if (seq.current !== mine || (e instanceof DOMException && e.name === 'AbortError')) return
        setState((s) => ({ status: s.data === undefined ? 'error' : 'ready', data: s.data, error: toFanslyError(e), refreshing: false }))
      })
    return () => {
      ctl.abort()
    }
  }, [api, key, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  const setData = useCallback((update: (prev: T | undefined) => T | undefined) => setState((s) => ({ ...s, data: update(s.data) })), [])
  return { ...state, reload, setData }
}
